/**
 * @description Applies cookie consent to third-party tracking and analytics.
 * @filename tracking-consent.ts
 * @summary Removing usage of UA and gtm module, as google requires GTM to be loaded in index.html, if left untouched
 * it would cause double triggers to fire.
 */
import { CookieConsentPreferences, getCookieConsentPreferences } from "./cookie-consent-storage";
import { ConsentStatus } from "./gtag-consent-policy";
import { consentStore, pushConsentToGtag } from "./gtag-consent-store";
let analyticsInitialized = false;
let marketingInitialized = false;

function isAnalyticsEnabled(): boolean {
  return process.env.ENABLE_ANALYTICS !== undefined;
}

export function applyTrackingConsent(preferences: CookieConsentPreferences): void {
  const analyticsState: ConsentStatus = preferences.analytics ? "granted" : "denied";
  const marketingState: ConsentStatus = preferences.marketing ? "granted" : "denied";
  consentStore.set("ad_user_data", marketingState);
  consentStore.set("ad_storage", marketingState);
  consentStore.set("ad_personalization", marketingState);
  consentStore.set("analytics_storage", analyticsState);
  pushConsentToGtag({
    analytics_storage: analyticsState,
    ad_storage: marketingState,
    ad_user_data: marketingState,
    ad_personalization: marketingState,
  });
}

function initializeAnalyticsTrackers(): void {
  if (analyticsInitialized) {
    return;
  }

  analyticsInitialized = true;

  setTimeout(() => {
    const HOTJAR = require("react-hotjar");
    HOTJAR.hotjar.initialize(process.env.HOTJAR_ID, process.env.HOTJAR_VERSION);
  }, 7 * 1000);
}

function initializeMarketingTrackers(): void {
  if (marketingInitialized) {
    return;
  }

  marketingInitialized = true;

  setTimeout(() => {
    const FACEBOOK_PIXEL = require("react-facebook-pixel").default;
    const advancedMatching = { em: "support@radivision.com" };
    const options = {
      autoConfig: true,
      debug: true,
    };

    FACEBOOK_PIXEL.init(process.env.FB_PIXEL_ID, advancedMatching, options);
    FACEBOOK_PIXEL.pageView();
  }, 7 * 1000);
}

/**
 * Initializes trackers allowed by the given preferences.
 * GTM is treated as a shared container and loads when either analytics or marketing is granted.
 */
export function initializeTracking(preferences?: CookieConsentPreferences | null): void {
  const consent = preferences || getCookieConsentPreferences();
  if (!consent) {
    return;
  }

  applyTrackingConsent(consent);

  if (!isAnalyticsEnabled()) {
    return;
  }

  if (consent.analytics) {
    initializeAnalyticsTrackers();
  }

  if (consent.marketing) {
    initializeMarketingTrackers();
  }
}

export function syncTrackingWithStoredConsent(): void {
  const preferences = getCookieConsentPreferences();

  if (!preferences) {
    return;
  }

  initializeTracking(preferences);
}
